import React, { useState } from "react";

/**
 * Gravity Ranking Challenge — multi-round class debate activity (Lesson 18).
 *
 * Each round gives six pairings to rank from strongest to weakest gravitational
 * pull. Groups reorder (up/down), optionally jot a mass/distance justification,
 * then LOCK IN their ranking before defending it to the class. No answers are
 * shown — the teacher adjudicates the debate. Advance through all rounds.
 *
 * Self-contained. Only dependency is React (hooks). Default export.
 * Next.js App Router: add  'use client'  as the first line of the file.
 *
 * Teacher keys are intentionally NOT in this file. See the accompanying notes.
 */

const C = {
  navy: "#1B2A4A", gold: "#C9A227", cream: "#F7F3E8", card: "#FBF8F0",
  blue: "#2E6E8E", terra: "#C0603A", ink: "#2B2B2B", mute: "#6B7280",
  line: "#E3DCC6", lockBg: "#eef3ec", lock: "#2f8f4e",
};

// Rounds of six pairings each. Order here is NOT the answer — it is displayed
// pre-shuffled, and the component never reveals a correct order.
const ROUNDS = [
  {
    name: "Round 1 — Around the Earth",
    blurb: "Earth is the heavy hitter. Sort by how close each thing is to it — and notice the two pairings that don’t include Earth at all.",
    items: [
      { id: "A", label: "Earth & a person standing on the surface" },
      { id: "B", label: "Earth & a nearby satellite in low orbit" },
      { id: "C", label: "Earth & the Moon" },
      { id: "D", label: "Earth & a distant spacecraft far beyond the Moon" },
      { id: "E", label: "Two students standing next to each other" },
      { id: "F", label: "Two bowling balls touching each other" },
    ],
  },
  {
    name: "Round 2 — You & the World",
    blurb: "Now you’re one of the objects. Watch what barely changes the pull… and what almost erases it.",
    items: [
      { id: "A", label: "Earth & you, standing on the ground" },
      { id: "B", label: "Earth & you, in an airplane at cruising altitude" },
      { id: "C", label: "Earth & you, aboard the Space Station" },
      { id: "D", label: "The Moon & you" },
      { id: "E", label: "You & a friend sitting right beside you" },
      { id: "F", label: "You & the phone in your pocket" },
    ],
  },
  {
    name: "Round 3 — Cosmic Heavyweights",
    blurb: "The tricky one. When a giant mass sits far away and a smaller one sits close, which wins? Argue it out.",
    items: [
      { id: "A", label: "The Sun & the Earth" },
      { id: "B", label: "The Sun & the Moon" },
      { id: "C", label: "The Earth & the Moon" },
      { id: "D", label: "The Moon & a small satellite orbiting it" },
      { id: "E", label: "Two spacecraft docking in orbit" },
      { id: "F", label: "Two pebbles a meter apart in deep space" },
    ],
  },
];

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function GravityRankingChallenge() {
  const [roundIdx, setRoundIdx] = useState(0);
  const [order, setOrder] = useState(() => shuffle(ROUNDS[0].items));
  const [reasons, setReasons] = useState({});
  const [locked, setLocked] = useState(false);

  const round = ROUNDS[roundIdx];
  const isLast = roundIdx === ROUNDS.length - 1;

  const move = (i, dir) => {
    if (locked) return;
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = order.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setOrder(next);
  };

  const startRound = (idx) => {
    setRoundIdx(idx);
    setOrder(shuffle(ROUNDS[idx].items));
    setReasons({});
    setLocked(false);
  };

  return (
    <div style={S.root}>
      <style>{`
        .gr-btn:focus-visible,.gr-move:focus-visible{outline:none;box-shadow:0 0 0 3px rgba(201,162,39,.45)}
        .gr-btn:active{transform:translateY(1px)}
        .gr-move:disabled{opacity:.3;cursor:default}
        .gr-why{font-family:inherit}
        .gr-why:focus-visible{outline:none;border-color:${C.gold};box-shadow:0 0 0 2px rgba(201,162,39,.25)}
      `}</style>

      <header style={S.band}>
        <div style={S.eyebrow}>Gravity · Lesson 18 Activity</div>
        <div style={S.title}>The Gravity Ranking Challenge</div>
        <div style={S.sub}>Rank the pairings, lock in your answer, then defend it to the class</div>
      </header>

      {/* round progress */}
      <div style={S.progress}>
        {ROUNDS.map((r, i) => (
          <div key={i} style={{
            ...S.pip,
            background: i === roundIdx ? C.gold : i < roundIdx ? C.blue : "#fff",
            color: i === roundIdx ? "#1a1300" : i < roundIdx ? "#fff" : C.mute,
            borderColor: i <= roundIdx ? "transparent" : C.line,
          }}>{i + 1}</div>
        ))}
        <div style={S.progressLabel}>{round.name}</div>
      </div>

      <div style={S.blurb}>{round.blurb}</div>

      <div style={S.ruleRow}>
        <div style={{ ...S.ruleBox, background: C.blue }}><b>Gravity LOVES mass.</b> More mass → stronger pull.</div>
        <div style={{ ...S.ruleBox, background: C.terra }}><b>Gravity HATES distance.</b> Farther apart → weaker pull (distance²).</div>
      </div>

      <div style={S.scaleHint}>
        <span style={{ color: C.blue, fontWeight: 700 }}>▲ STRONGEST pull</span>
        <span style={{ color: C.terra, fontWeight: 700 }}>WEAKEST pull ▼</span>
      </div>

      <ol style={S.list}>
        {order.map((s, i) => (
          <li key={s.id} style={{ ...S.rowCard, borderColor: locked ? C.lock : C.line, background: locked ? C.lockBg : C.card }}>
            <div style={S.rank}>{i + 1}</div>
            <div style={S.rowMain}>
              <div style={S.rowLabel}><span style={S.letter}>{s.id}</span>{s.label}</div>
              <input
                className="gr-why"
                style={S.why}
                placeholder="Our reasoning — name the mass and the distance…"
                value={reasons[s.id] || ""}
                onChange={(e) => setReasons((r) => ({ ...r, [s.id]: e.target.value }))}
                disabled={locked}
              />
            </div>
            <div style={S.moveCol}>
              <button className="gr-move" style={S.move} onClick={() => move(i, -1)} disabled={locked || i === 0} aria-label="Move up">▲</button>
              <button className="gr-move" style={S.move} onClick={() => move(i, 1)} disabled={locked || i === order.length - 1} aria-label="Move down">▼</button>
            </div>
          </li>
        ))}
      </ol>

      {!locked ? (
        <div style={S.controls}>
          <button className="gr-btn" style={{ ...S.btn, ...S.btnPrimary }} onClick={() => setLocked(true)}>🔒 Lock in our ranking</button>
          <button className="gr-btn" style={S.btn} onClick={() => setOrder(shuffle(round.items))}>Shuffle</button>
        </div>
      ) : (
        <>
          <div style={S.lockedBanner}>
            🔒 Locked in. Now defend your order to the class — every placement needs a reason using <b>mass</b> and <b>distance</b>. You may only change your mind if another group’s science beats yours.
          </div>
          <div style={S.controls}>
            {!isLast ? (
              <button className="gr-btn" style={{ ...S.btn, ...S.btnPrimary }} onClick={() => startRound(roundIdx + 1)}>Next round →</button>
            ) : (
              <div style={S.doneMsg}>🏁 Final round complete — great debating!</div>
            )}
            <button className="gr-btn" style={S.btn} onClick={() => setLocked(false)}>Unlock to adjust</button>
            <button className="gr-btn" style={S.btn} onClick={() => startRound(0)}>Restart from Round 1</button>
          </div>
        </>
      )}
    </div>
  );
}

const S = {
  root: { maxWidth: 760, margin: "0 auto", padding: 16, boxSizing: "border-box", fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif", color: C.ink },
  band: { background: C.navy, borderRadius: 12, padding: "16px 20px", marginBottom: 14 },
  eyebrow: { fontSize: 11, letterSpacing: 2.5, textTransform: "uppercase", color: C.gold, fontWeight: 700, marginBottom: 4 },
  title: { fontFamily: "Georgia,serif", fontSize: "clamp(20px,4vw,28px)", fontWeight: 700, color: C.cream },
  sub: { fontSize: 14, fontStyle: "italic", color: "#E0D8BE", marginTop: 4 },
  progress: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" },
  pip: { width: 28, height: 28, borderRadius: "50%", border: "1.5px solid", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, fontFamily: "Georgia,serif" },
  progressLabel: { fontFamily: "Georgia,serif", fontWeight: 700, color: C.navy, fontSize: 16, marginLeft: 4 },
  blurb: { fontSize: 14, color: C.ink, lineHeight: 1.5, marginBottom: 14, padding: "10px 14px", background: C.card, border: `1px solid ${C.line}`, borderRadius: 10 },
  ruleRow: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 },
  ruleBox: { flex: "1 1 240px", minWidth: 0, color: C.cream, fontSize: 13.5, lineHeight: 1.45, padding: "10px 14px", borderRadius: 10 },
  scaleHint: { display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 8, padding: "0 4px" },
  list: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 },
  rowCard: { display: "flex", alignItems: "stretch", border: "1.5px solid", borderRadius: 10, overflow: "hidden" },
  rank: { flex: "none", width: 48, background: C.navy, color: C.gold, fontFamily: "Georgia,serif", fontSize: 22, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" },
  rowMain: { flex: 1, minWidth: 0, padding: "10px 14px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 6 },
  rowLabel: { fontFamily: "Georgia,serif", fontSize: 15.5, fontWeight: 700, color: C.navy, lineHeight: 1.3 },
  letter: { display: "inline-block", minWidth: 20, color: C.gold, fontWeight: 700 },
  why: { width: "100%", boxSizing: "border-box", fontSize: 12.5, color: C.ink, padding: "5px 8px", border: `1px solid ${C.line}`, borderRadius: 6, background: "#fff" },
  moveCol: { flex: "none", display: "flex", flexDirection: "column", justifyContent: "center", gap: 4, padding: "0 10px" },
  move: { width: 34, height: 28, borderRadius: 6, border: `1px solid ${C.line}`, background: "#fff", color: C.navy, cursor: "pointer", fontSize: 12, lineHeight: 1 },
  controls: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16, alignItems: "center" },
  btn: { fontSize: 13, fontWeight: 600, padding: "10px 16px", borderRadius: 8, cursor: "pointer", border: `1px solid ${C.line}`, background: "#fff", color: C.navy, fontFamily: "inherit" },
  btnPrimary: { background: C.gold, color: "#1a1300", borderColor: C.gold },
  lockedBanner: { marginTop: 14, padding: "12px 16px", borderRadius: 10, border: `1.5px solid ${C.lock}`, background: C.lockBg, fontSize: 14, lineHeight: 1.5 },
  doneMsg: { fontFamily: "Georgia,serif", fontWeight: 700, color: C.navy, fontSize: 15 },
};